import { deleteObject, getDownloadURL, ref, uploadBytes, type FirebaseStorage } from 'firebase/storage';
import type { MediaRepository } from '@nx/domain';

function storagePathFromDownloadUrl(downloadUrl: string): string | null {
  try {
    const parsed = new URL(downloadUrl);
    const marker = '/o/';
    const idx = parsed.pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(parsed.pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
}

export class FirebaseMediaRepository implements MediaRepository {
  constructor(private readonly storage: FirebaseStorage) {}

  private async upload(path: string, file: Blob): Promise<string> {
    const storageRef = ref(this.storage, path);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }

  async uploadEventCover(
    organizerId: string,
    eventId: string,
    file: Blob,
    fileName: string,
  ): Promise<string> {
    return this.upload(`events/${organizerId}/${eventId}/cover/${fileName}`, file);
  }

  async uploadPartnerLogo(
    eventId: string,
    partnerId: string,
    file: Blob,
    fileName: string,
  ): Promise<string> {
    return this.upload(`events/${eventId}/partners/${partnerId}/logo/${fileName}`, file);
  }

  async uploadPartnerContract(
    eventId: string,
    partnerId: string,
    file: Blob,
    fileName: string,
  ): Promise<string> {
    return this.upload(`events/${eventId}/partners/${partnerId}/contract/${fileName}`, file);
  }

  async uploadPosterAsset(eventId: string, file: Blob, fileName: string): Promise<string> {
    return this.upload(`events/${eventId}/poster/${fileName}`, file);
  }

  async uploadProfileAvatar(userId: string, file: Blob, fileName: string): Promise<string> {
    return this.upload(`users/${userId}/profile/avatar/${fileName}`, file);
  }

  async uploadProfileBackground(
    userId: string,
    profileId: string,
    file: Blob,
    fileName: string,
  ): Promise<string> {
    return this.upload(`users/${userId}/profile/${profileId}/background/${fileName}`, file);
  }

  async uploadProfileComponentImage(
    userId: string,
    profileId: string,
    componentId: string,
    file: Blob,
    fileName: string,
  ): Promise<string> {
    return this.upload(
      `users/${userId}/profile/${profileId}/components/${componentId}/${fileName}`,
      file,
    );
  }

  async deleteByUrl(downloadUrl: string): Promise<void> {
    const path = storagePathFromDownloadUrl(downloadUrl);
    if (!path) throw new Error('Invalid storage download URL');
    await deleteObject(ref(this.storage, path));
  }
}
